import { useEffect, useMemo, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { ErrorState, Skeleton } from '@/components/feedback';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import { trackPublicPropertyAction } from '@/lib/analytics/events';
import { Seo } from '@/components/seo';
import { usePublicProperty } from '@/hooks/usePublicProperties';
import { useSiteSettingsDetail } from '@/lib/query/resources/siteSettings';
import type {
  PublicPropertyDetail as PublicPropertyDetailResult,
  PublicPropertySummary,
  PublishedUnitSummary,
} from '@/lib/types/publicProperty';
import type {
  PropertyAddress,
  UnitAvailabilityStatus,
  UnitPossessionTimeline,
} from '@/lib/types/property';
import {
  formatAvailabilityDateLabel,
  formatPropertyAddress,
  formatSquareFeet,
  formatUnitLabel,
  humanizeAddressText,
} from '@/lib/utils/propertyFormatting';
import { applyTemplate } from '@/lib/utils/textTemplates';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import { recordRecentUnitView } from '@/lib/utils/recentUnits';
import type { UnitDetailContent } from '@/lib/types/siteSettings';
import { formatSelectedUnitLabel } from './components/UnitEngagementDialog';
import { UnitShowingTriggerButton } from './components/UnitEngagementDialog';
import { PublicDetailHero } from './components/PublicDetailHero';
import { formatEstimatedTotalLabel, formatTotalRent } from './rentFormatting';

const DEFAULT_UNIT_DETAIL_CONTENT: UnitDetailContent = {
  messages: {
    notFoundMessage: 'Unit not found or no longer published.',
    unavailableMessage: 'This unit is no longer available.',
  },
  actions: {
    viewPropertyLabel: 'View property listings',
    browseAvailabilitiesLabel: 'Browse availabilities',
    viewOtherUnitsLabel: 'View other units',
    stickyEyebrow: 'Schedule a showing',
    stickyButtonLabel: 'Book now',
  },
  headings: {
    atAGlanceTitle: 'At a glance',
    propertyDetailsTitle: 'Property Details',
    leaseDetailsTitle: 'Lease Details',
    propertyDescriptionEyebrow: 'Property description',
    propertyDescriptionTitle: 'About this property',
    scheduleEyebrow: 'Schedule a showing',
    scheduleTitle: '{unitLabel}',
    leasingDetailsEyebrow: 'Leasing details',
    leasingDetailsTitle: 'Confirm the details that matter',
    photoGalleryTitle: 'Photo Gallery',
  },
  labels: {
    sizeLabel: 'Size',
    floorLabel: 'Floor',
    divisibleLabel: 'Divisible',
    statusLabel: 'Status',
    availabilityLabel: 'Availability',
    possessionLabel: 'Possession',
    updatedLabel: 'Updated',
    immediateLabel: 'Immediate',
    negotiableLabel: 'Negotiable',
    immediatePossessionLabel: 'Immediate possession',
    negotiablePossessionLabel: 'Negotiable possession',
    updatedRecentlyLabel: 'Updated recently',
  },
  leasingPrompts: {
    industrial:
      "Ask about loading access, clear height, power, parking, and any available incentives. We'll confirm what applies to this suite.",
    flex: "Ask about loading access, clear height, power, parking, and any available incentives. We'll confirm what applies to this suite.",
    retail:
      "Ask about storefront exposure, signage options, utilities, delivery access, and any available incentives. We'll confirm what applies to this suite.",
    office:
      "Ask about build-out options, parking, transit access, building services, and any available incentives. We'll confirm what applies to this suite.",
    fallback:
      "Ask about fit-up options, building services, parking, and any available incentives. We'll confirm what applies to this suite.",
  },
  photoGalleryEmptyMessage: 'Photos coming soon',
};

export function PublicUnitDetail(): JSX.Element {
  const { propertySlug, unitId } = useParams<{ propertySlug: string; unitId: string }>();
  const propertySlugSafe: string = typeof propertySlug === 'string' ? propertySlug : '';
  const unitIdSafe: string = typeof unitId === 'string' ? unitId : '';
  const identifier = useMemo(() => propertySlugSafe.trim().toUpperCase(), [propertySlugSafe]);
  const request = identifier.length > 0 ? { identifier } : null;
  const { data, isLoading, isFetching, error } = usePublicProperty(request);
  const siteSettingsQuery = useSiteSettingsDetail();
  const unitDetailContent = mergeContentWithDefaults(
    siteSettingsQuery.data?.publicContent.unitDetail ?? null,
    DEFAULT_UNIT_DETAIL_CONTENT,
  );
  const summary = data?.summary ?? null;
  const selectedUnit = useMemo(() => {
    if (summary === null) {
      return null;
    }
    return summary.publishedUnits.find((entry) => entry.id === unitIdSafe) ?? null;
  }, [summary, unitIdSafe]);
  const canonicalPath =
    propertySlugSafe.length > 0 && unitIdSafe.length > 0
      ? `/properties/${propertySlugSafe.toLowerCase()}/units/${unitIdSafe}`
      : '/availabilities';
  const seoTitle =
    summary && selectedUnit
      ? `${formatUnitLabel(selectedUnit)} at ${formatPropertyAddress(summary.address)}`
      : 'Unit detail';
  const marketingSummary =
    summary !== null && summary.marketingSummary.trim().length > 0
      ? summary.marketingSummary
      : null;
  const seoDescription = marketingSummary ?? unitDetailContent.messages.notFoundMessage;
  const canonicalUrl = resolveCanonicalUrl(canonicalPath);
  const structuredData = useMemo(
    () =>
      summary && selectedUnit ? buildUnitStructuredData(summary, selectedUnit, canonicalUrl) : null,
    [canonicalUrl, selectedUnit, summary],
  );

  return (
    <PublicPageLayout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        structuredData={structuredData}
      />
      <PublicPageSection
        tone="base"
        spacing="none"
        stacked={false}
        aria-label="Unit details"
        containerClassName="flex flex-col gap-8 pb-24 md:gap-10"
        className="overflow-hidden"
      >
        <AsyncState
          data={data ?? null}
          isLoading={isLoading || isFetching}
          error={error}
          skeleton={<UnitDetailSkeleton />}
          errorFallback={(caught) => {
            const message =
              caught instanceof Error ? caught.message : unitDetailContent.messages.notFoundMessage;
            return (
              <div className="flex flex-col gap-4">
                <ErrorState description={message} />
                <p className="text-sm text-text-primary/70">
                  {unitDetailContent.messages.notFoundMessage}{' '}
                  <Link to="/availabilities" className="font-semibold text-brand-primary underline">
                    {unitDetailContent.actions.browseAvailabilitiesLabel}
                  </Link>
                </p>
              </div>
            );
          }}
        >
          {(detail) => (
            <UnitDetailContent
              detail={detail}
              unitId={unitId ?? ''}
              propertySlug={propertySlugSafe}
              content={unitDetailContent}
            />
          )}
        </AsyncState>
      </PublicPageSection>
    </PublicPageLayout>
  );
}

function UnitDetailContent({
  detail,
  unitId,
  propertySlug,
  content,
}: {
  detail: PublicPropertyDetailResult;
  unitId: string;
  propertySlug: string;
  content: UnitDetailContent;
}): JSX.Element {
  const propertySlugSafe: string = typeof propertySlug === 'string' ? propertySlug : '';
  const { summary } = detail;
  const unit = useMemo(
    () => summary.publishedUnits.find((entry) => entry.id === unitId) ?? null,
    [summary.publishedUnits, unitId],
  );

  useEffect(() => {
    if (!unit) {
      return;
    }
    trackPublicPropertyAction({
      propertyId: summary.id,
      propertyCode: summary.propertyCode,
      action: 'unit_view',
      unitId: unit.id,
      readiness: unit.availabilityStatus,
    });
    recordRecentUnitView({ propertyCode: summary.propertyCode, unitId: unit.id });
  }, [summary.id, summary.propertyCode, unit]);

  if (unit === null) {
    const propertyPath =
      propertySlugSafe.length > 0
        ? `/properties/${propertySlugSafe.toLowerCase()}`
        : `/properties/${summary.propertyCode.toLowerCase()}`;
    return (
      <div className="flex flex-col gap-4 border border-dashed border-surface-subtle bg-surface-subtle/40 p-6">
        <ErrorState description={content.messages.notFoundMessage} />
        <div className="flex flex-wrap gap-3">
          <Link
            to={propertyPath}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-primary shadow-sm ring-1 ring-surface-subtle transition hover:text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
          >
            {content.actions.viewPropertyLabel}
          </Link>
          <Link
            to="/availabilities"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-brand-accent underline underline-offset-4 transition hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
          >
            {content.actions.browseAvailabilitiesLabel}
          </Link>
        </div>
      </div>
    );
  }

  const hero = resolveHero(summary);
  const areaLabel = formatArea(unit.areaSF, unit.minDivisibleSF);
  const availabilityLabel = formatAvailability(
    unit.availabilityStart,
    unit.possessionTimeline,
    content.labels,
  );
  const rentLabel = formatTotalRent(unit);
  const rentTotalLabel = formatEstimatedTotalLabel(unit.rentFrequency);
  const rentTotalValue = formatTotalRent(unit, { includeFrequency: false });
  const statusUpdatedLabel = formatDate(unit.statusUpdatedAt, content.labels);
  const gallery = buildGallery(summary, unit);
  const galleryCount = gallery.length;
  const mapAddress = formatPropertyAddress(summary.address);
  const propertyDetails: Array<{ label: string; value: string }> = [
    { label: content.labels.sizeLabel, value: areaLabel },
    { label: content.labels.floorLabel, value: unit.floorLevel },
    {
      label: content.labels.divisibleLabel,
      value: unit.potentiallyDivisible ? 'Yes' : 'No',
    },
    { label: content.labels.statusLabel, value: PUBLIC_READINESS_LABELS[unit.availabilityStatus] },
  ];
  const leaseDetails: Array<{ label: string; value: string }> = [
    { label: rentTotalLabel, value: rentTotalValue },
    { label: content.labels.availabilityLabel, value: availabilityLabel },
    { label: content.labels.updatedLabel, value: statusUpdatedLabel },
  ];
  const marketingSummary = summary.marketingSummary;
  const leasingDetailsPrompt = formatLeasingDetailsPrompt(
    summary.assetType,
    content.leasingPrompts,
  );
  const unitLabel = formatSelectedUnitLabel(summary, unit);
  const scheduleTitle = applyTemplate(content.headings.scheduleTitle, { unitLabel });

  const handleCtaClick = () => {
    trackPublicPropertyAction({
      propertyId: summary.id,
      propertyCode: summary.propertyCode,
      action: 'unit_cta_click',
      unitId: unit.id,
      readiness: unit.availabilityStatus,
    });
  };

  const handleCtaBlocked = (_unit: PublishedUnitSummary, reason: string | null) => {
    trackPublicPropertyAction({
      propertyId: summary.id,
      propertyCode: summary.propertyCode,
      action: 'unit_cta_blocked',
      unitId: unit.id,
      readiness: unit.availabilityStatus,
      reason: reason ?? 'Unit unavailable',
    });
  };

  return (
    <article className="relative flex flex-col gap-6 md:gap-8">
      <StickyCtaBar
        summary={summary}
        unit={unit}
        availabilityLabel={availabilityLabel}
        rentLabel={rentLabel}
        unitLabel={unitLabel}
        content={content}
        onCtaClick={handleCtaClick}
        onCtaBlocked={handleCtaBlocked}
      />

      <PublicDetailHero
        cover={hero}
        badges={[formatPropertyAddress(summary.address), summary.submarket]}
        title={`${formatUnitLabel(unit)} · ${content.labels.floorLabel} ${unit.floorLevel}`}
        meta={[rentLabel, availabilityLabel].filter(Boolean)}
        galleryCount={galleryCount > 1 ? galleryCount : undefined}
        heightClassName="h-[300px] md:h-[420px]"
      />

      <section className="border border-surface-subtle bg-white p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-brand-primary">
            {content.headings.atAGlanceTitle}
          </h2>
          <ReadinessPill status={unit.availabilityStatus} />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <DetailList title={content.headings.propertyDetailsTitle} items={propertyDetails} />
          <div data-ui="Suites.RentSummary">
            <DetailList title={content.headings.leaseDetailsTitle} items={leaseDetails} />
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-4 border border-surface-subtle bg-white p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {content.headings.propertyDescriptionEyebrow}
            </p>
            <h2 className="text-xl font-semibold text-brand-primary">
              {content.headings.propertyDescriptionTitle}
            </h2>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-text-primary/80">{marketingSummary}</p>
      </section>

      <section className="flex flex-col gap-4 border border-surface-subtle bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {content.headings.scheduleEyebrow}
            </p>
            <h3 className="text-lg font-semibold text-brand-primary">{scheduleTitle}</h3>
          </div>
          <UnitShowingTriggerButton
            summary={summary}
            unit={unit}
            className="min-w-[160px] text-center"
            label={content.actions.stickyButtonLabel}
            onCtaClick={handleCtaClick}
            onCtaBlocked={handleCtaBlocked}
          />
        </div>
      </section>

      <section className="grid gap-6 border border-surface-subtle bg-white p-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
            {content.headings.leasingDetailsEyebrow}
          </p>
          <h3 className="text-lg font-semibold text-brand-primary">
            {content.headings.leasingDetailsTitle}
          </h3>
          <p className="text-sm leading-relaxed text-text-primary/80">{leasingDetailsPrompt}</p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-brand-primary">
              {content.headings.photoGalleryTitle}
            </h3>
            {gallery.length > 0 ? (
              <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {gallery.length} photos
              </span>
            ) : null}
          </div>
          {gallery.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {gallery.map((item, index) => (
                <figure key={index} className="overflow-hidden border border-surface-subtle">
                  <img
                    src={item.url}
                    alt={item.alt}
                    className="h-28 w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
              ))}
            </div>
          ) : (
            <div className="flex h-32 items-center justify-center border border-dashed border-surface-subtle bg-surface-subtle/40 text-sm text-text-primary/70">
              {content.photoGalleryEmptyMessage}
            </div>
          )}
        </div>
      </section>

      <section className="border border-surface-subtle bg-white">
        <div className="h-full w-full">
          <iframe
            title={`Map for ${mapAddress}`}
            src={buildMapEmbedUrl(mapAddress)}
            className="h-[320px] w-full border-0"
            loading="lazy"
          />
        </div>
      </section>
    </article>
  );
}

function DetailList({
  title,
  items,
}: {
  title: string;
  items: Array<{ label: string; value: string }>;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">{title}</p>
      <dl className="divide-y divide-surface-subtle border border-surface-subtle">
        {items.map((item) => (
          <div
            key={item.label}
            className="grid grid-cols-[1fr_1.3fr] items-center gap-3 px-4 py-3 text-sm text-text-primary"
          >
            <dt className="font-semibold text-text-primary/70">{item.label}</dt>
            <dd className="font-semibold text-text-primary">{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function formatLeasingDetailsPrompt(
  assetType: PublicPropertySummary['assetType'],
  prompts: UnitDetailContent['leasingPrompts'],
): string {
  switch (assetType) {
    case 'industrial':
    case 'flex':
      return assetType === 'flex' ? prompts.flex : prompts.industrial;
    case 'retail':
      return prompts.retail;
    case 'office':
      return prompts.office;
    default:
      return prompts.fallback;
  }
}

function StickyCtaBar({
  summary,
  unit,
  availabilityLabel,
  rentLabel,
  unitLabel,
  content,
  onCtaClick,
  onCtaBlocked,
}: {
  summary: PublicPropertySummary;
  unit: PublishedUnitSummary;
  availabilityLabel: string;
  rentLabel: string;
  unitLabel: string;
  content: UnitDetailContent;
  onCtaClick: () => void;
  onCtaBlocked: (_unit: PublishedUnitSummary, reason: string | null) => void;
}): JSX.Element {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-surface-subtle bg-white px-4 py-3 shadow-[0_-10px_30px_rgba(0,0,0,0.12)]">
      <div className="container flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {content.actions.stickyEyebrow}
          </p>
          <p className="text-sm font-semibold text-brand-primary">{unitLabel}</p>
          <p className="text-xs text-text-primary/70">
            {availabilityLabel} · {rentLabel}
          </p>
        </div>
        <UnitShowingTriggerButton
          summary={summary}
          unit={unit}
          className="w-full sm:w-auto"
          label={content.actions.stickyButtonLabel}
          onCtaClick={onCtaClick}
          onCtaBlocked={onCtaBlocked}
        />
      </div>
    </div>
  );
}

function UnitDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-80 w-full" />
      <div className="flex flex-col gap-4 border border-surface-subtle bg-white p-6">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 9 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  );
}

function ReadinessPill({ status }: { status: UnitAvailabilityStatus }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-primary">
      <span className="h-2 w-2 rounded-full bg-brand-primary" aria-hidden="true" />
      {PUBLIC_READINESS_LABELS[status]}
    </span>
  );
}

function resolveHero(summary: PublicPropertySummary): { url: string; alt: string } | null {
  if (typeof summary.coverImageUrl === 'string' && summary.coverImageUrl.trim().length > 0) {
    return {
      url: summary.coverImageUrl,
      alt: summary.coverImageAlt ?? formatPropertyAddress(summary.address),
    };
  }

  const fallback = summary.heroMediaUrls[0];
  if (typeof fallback === 'string' && fallback.trim().length > 0) {
    return { url: fallback, alt: formatPropertyAddress(summary.address) };
  }

  return null;
}

function buildGallery(
  summary: PublicPropertySummary,
  unit: PublishedUnitSummary,
): Array<{ url: string; alt: string }> {
  const unitGalleryUrls = Array.isArray(unit.galleryImageUrls)
    ? unit.galleryImageUrls.map((url) => url.trim()).filter((url) => url.length > 0)
    : [];
  const heroUrl =
    typeof unit.heroImageUrl === 'string' && unit.heroImageUrl.trim().length > 0
      ? unit.heroImageUrl.trim()
      : null;
  const combinedUnitUrls = [heroUrl, ...unitGalleryUrls].filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
  const dedupedUnitUrls = Array.from(new Set(combinedUnitUrls));
  const urls = dedupedUnitUrls.length > 0 ? dedupedUnitUrls : summary.heroMediaUrls;
  return urls.slice(0, 6).map((url, index) => ({
    url,
    alt: `Photo ${String(index + 1)} for ${formatPropertyAddress(summary.address)}`,
  }));
}

function formatAvailability(
  availabilityStart: string | null | undefined,
  possessionTimeline: UnitPossessionTimeline | null | undefined,
  labels: UnitDetailContent['labels'],
): string {
  const availabilityDate = formatAvailabilityDateLabel(availabilityStart, {
    nowLabel: 'Available now',
  });
  if (availabilityDate) {
    return availabilityDate;
  }
  if (possessionTimeline === 'negotiable') {
    return labels.negotiableLabel;
  }
  return labels.immediateLabel;
}

function formatDate(value: string | null | undefined, labels: UnitDetailContent['labels']): string {
  if (typeof value !== 'string' || value.length === 0) {
    return labels.updatedRecentlyLabel;
  }
  try {
    return new Intl.DateTimeFormat('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(value));
  } catch {
    return labels.updatedRecentlyLabel;
  }
}

function formatArea(area: number, minDivisible: number | null): string {
  const base = `${formatSquareFeet(area)} sq ft`;
  if (minDivisible !== null && minDivisible !== area) {
    return `${base} - Min divisible ${formatSquareFeet(minDivisible)} sq ft`;
  }
  return base;
}

function buildMapEmbedUrl(address: string): string {
  const query = encodeURIComponent(address);
  return `https://www.google.com/maps?q=${query}&output=embed`;
}

function buildPostalAddress(address: PropertyAddress): Record<string, string> {
  const line1 = [
    humanizeAddressText(address.streetNumber),
    humanizeAddressText(address.streetName),
    humanizeAddressText(address.streetType),
    humanizeAddressText(address.streetDirection),
  ]
    .filter(Boolean)
    .join(' ');
  const suite = humanizeAddressText(address.suiteNumber ?? '');
  const streetAddress = suite.length > 0 ? `${suite} ${line1}` : line1;

  return {
    '@type': 'PostalAddress',
    streetAddress,
    addressLocality: humanizeAddressText(address.city),
    addressRegion: humanizeAddressText(address.province),
    postalCode: humanizeAddressText(address.postalCode),
    addressCountry: 'CA',
  };
}

function buildUnitStructuredData(
  summary: PublicPropertySummary,
  unit: PublishedUnitSummary,
  canonicalUrl: string,
): Record<string, unknown> {
  const images = [
    unit.heroImageUrl,
    ...unit.galleryImageUrls,
    summary.coverImageUrl,
    ...summary.heroMediaUrls,
  ]
    .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    .map((url) => url.trim());
  const availability =
    unit.availabilityStatus === 'available'
      ? 'https://schema.org/InStock'
      : unit.availabilityStatus === 'leased'
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/OutOfStock';

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: `${formatUnitLabel(unit)} at ${formatPropertyAddress(summary.address)}`,
    description: summary.marketingSummary,
    url: canonicalUrl,
    image: images.length > 0 ? images : undefined,
    address: buildPostalAddress(summary.address),
    category: summary.assetType,
    floorSize: {
      '@type': 'QuantitativeValue',
      value: unit.areaSF,
      unitCode: 'SF',
    },
    offers: {
      '@type': 'Offer',
      availability,
    },
  };
}

function resolveCanonicalUrl(path: string): string {
  if (typeof window === 'undefined') {
    return path;
  }
  try {
    return new URL(path, window.location.origin).href;
  } catch {
    return path;
  }
}
